/**
 * Hook que cachea la lista completa de gabinetes (raramente cambian) en una
 * variable de módulo y comparte el resultado entre componentes para evitar
 * peticiones duplicadas dentro de la misma sesión.
 *
 * Devuelve `{ map, byId }` donde:
 *   - `map`: `Map<gabinete_id, Gabinete>` listo para `map.get(id)`.
 *   - `byId(id)`: helper conveniente; retorna `undefined` si no existe.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let CACHE = null;          // Array<Gabinete> | null
let CACHE_PROMISE = null;  // Promise pendiente (deduplica fetches simultáneos)

export function useGabinetesMap() {
  const [list, setList] = useState(CACHE);

  useEffect(() => {
    if (CACHE) { setList(CACHE); return; }
    if (!CACHE_PROMISE) {
      CACHE_PROMISE = api.get("/gabinetes")
        .then((r) => { CACHE = r.data || []; return CACHE; })
        .catch(() => { CACHE = []; return CACHE; })
        .finally(() => { CACHE_PROMISE = null; });
    }
    let cancelled = false;
    CACHE_PROMISE.then((v) => { if (!cancelled) setList(v); });
    return () => { cancelled = true; };
  }, []);

  const map = new Map((list || []).map((g) => [g.id, g]));
  return {
    map,
    byId: (id) => map.get(id),
  };
}
